/// <amd-module name="Artifacts/Package/redux/Reducer" />
import {
  IFakeDeletePackagePayload,
  IFakeDeprecatePackagePayload,
  IFakeListUnlistPackagePayload,
  IFakePromotePackagePayload,
  IFollowActionCompletedPayload,
  IIngestFromUpstreamCompletedPayload,
  ILoadPackageDetailsCompletedPayload,
  ILoadUpstreamVersionsCompletedPayload,
  PackageActions,
  PackageActionTypes,
} from "Artifacts/Package/redux/Actions";
import { IPackageDetailsState } from "Artifacts/Package/redux/Contracts";
import { Protocol } from "Artifacts/Root/Constants";
import {
  LoadingComponentState,
  ReadyComponentState,
} from "Artifacts/Root/helpers/ComponentStateHelpers";
import { syncComponentState } from "Artifacts/Root/Redux";
import produce from "immer";
import { ListSelection } from "VSSUI/List";

const initialState = (): IPackageDetailsState => ({
  ui: {
    packageDetailsComponentState: LoadingComponentState(),
    upstreamIngestionComponentState: ReadyComponentState(),
    upstreamVersionsComponentState: LoadingComponentState(),
    packageListSelection: new ListSelection({ selectOnFocus: false, multiSelect: true }),
    followState: false,
    upstreamOverrideCommandState: ReadyComponentState(),
    upstreamOverrideModal: ReadyComponentState(),
  },
  data: {},
});

export const packageReducer = produce((draft: IPackageDetailsState, action: PackageActions) => {
  syncComponentState(draft, "ui.packageDetailsComponentState", action, {
    ready: PackageActionTypes.LoadPackageDetailsCompleted,
    loading: PackageActionTypes.LoadPackageDetails,
    error: PackageActionTypes.LoadPackageDetailsFailed,
  });
  syncComponentState(draft, "ui.upstreamVersionsComponentState", action, {
    ready: PackageActionTypes.LoadUpstreamVersionsCompleted,
    loading: PackageActionTypes.LoadUpstreamVersions,
    error: PackageActionTypes.LoadUpstreamVersionsFailed,
  });
  syncComponentState(draft, "upstreamOverrideCommandState", action, {
    loading: PackageActionTypes.UpstreamOverrideCommandStarted,
    ready: PackageActionTypes.UpstreamOverrideCommandCompleted,
    error: PackageActionTypes.UpstreamOverrideCommandFailed,
  });
  syncComponentState(draft, "upstreamOverrideModal", action, {
    loading: PackageActionTypes.UpstreamOverrideModalStarted,
    ready: PackageActionTypes.UpstreamOverrideModalCompleted,
    error: PackageActionTypes.UpstreamOverrideModalFailed,
  });
  syncComponentState(draft, "ui.upstreamIngestionComponentState", action, {
    loading: PackageActionTypes.IngestFromUpstreamStarted,
    ready: PackageActionTypes.IngestFromUpstreamCompleted,
    error: PackageActionTypes.IngestFromUpstreamFailed,
  });
  switch (action.type) {
    case PackageActionTypes.LoadPackageDetailsCompleted:
      loadPackageDetailsCompleted(draft, action.payload as ILoadPackageDetailsCompletedPayload);
      break;
    case PackageActionTypes.LoadUpstreamVersionsCompleted:
      loadUpstreamVersionsCompleted(draft, action.payload as ILoadUpstreamVersionsCompletedPayload);
      break;
    case PackageActionTypes.IngestFromUpstreamCompleted:
      fakePackageIngestion(draft, action.payload as IIngestFromUpstreamCompletedPayload);
      break;
    case PackageActionTypes.GetFollowStateCompleted:
    case PackageActionTypes.SetFollowStateCompleted:
      draft.ui.followState = (action.payload as IFollowActionCompletedPayload).followingState;
      break;
    case PackageActionTypes.FakePackagePromote:
      fakePackagePromote(draft, action.payload as IFakePromotePackagePayload);
      break;
    case PackageActionTypes.FakePackageDelete:
      fakePackageDelete(draft, action.payload as IFakeDeletePackagePayload);
      break;
    case PackageActionTypes.FakePackageListUnlist:
      fakePackageListUnlist(draft, action.payload as IFakeListUnlistPackagePayload);
      break;
    case PackageActionTypes.FakePackageDeprecate:
      fakePackageDeprecate(draft, action.payload as IFakeDeprecatePackagePayload);
      break;
    case PackageActionTypes.UpstreamOverrideCommandCompleted:
    case PackageActionTypes.UpstreamOverrideModalCompleted:
      draft.ui.upstreamOverrideStatus = action.payload as boolean;
      break;
  }
  return draft;
}, initialState());

function fakePackagePromote(
  draft: IPackageDetailsState,
  payload: IFakePromotePackagePayload,
): void {
  payload.selectedVersions.forEach((sv) => {
    const packageVersion = draft.data.packageVersionsList?.find(
      (v) => v.normalizedVersion === sv.normalizedVersion,
    );
    if (packageVersion && packageVersion.views.indexOf(payload.viewName) < 0) {
      packageVersion.views.push(payload.viewName);
    }

    if (
      draft.data.currentPackageVersion?.normalizedVersion === sv.normalizedVersion &&
      draft.data.currentPackageVersion.views.indexOf(payload.viewName) < 0
    ) {
      draft.data.currentPackageVersion.views.push(payload.viewName);
    }
  });
}

function fakePackageDelete(draft: IPackageDetailsState, payload: IFakeDeletePackagePayload) {
  payload.selectedVersions.forEach((sv) => {
    const packageVersion = draft.data.packageVersionsList?.find(
      (v) => v.normalizedVersion === sv.version,
    );
    if (packageVersion) {
      packageVersion.isDeleted = true;
    }

    if (draft.data.currentPackageVersion?.normalizedVersion === sv.version) {
      draft.data.currentPackageVersion.isDeleted = true;
    }
  });
}

function fakePackageListUnlist(
  draft: IPackageDetailsState,
  payload: IFakeListUnlistPackagePayload,
) {
  payload.selectedVersions.forEach((sv) => {
    const packageVersion = draft.data.packageVersionsList?.find(
      (v) => v.normalizedVersion === sv.version,
    );
    if (packageVersion) {
      packageVersion.isListed = payload.isListed;
    }

    if (draft.data.currentPackageVersion?.normalizedVersion === sv.version) {
      draft.data.currentPackageVersion.isListed = payload.isListed;
    }
  });
}

function fakePackageDeprecate(draft: IPackageDetailsState, payload: IFakeDeprecatePackagePayload) {
  payload.selectedVersions.forEach((sv) => {
    if (
      draft.data.currentPackageVersion?.normalizedVersion === sv.version &&
      sv.protocol.toLowerCase() === Protocol.Npm
    ) {
      draft.data.currentPackageVersion.protocolMetadata.data.deprecated = payload.deprecateMessage;
    }
  });
}

function fakePackageIngestion(
  draft: IPackageDetailsState,
  payload: IIngestFromUpstreamCompletedPayload,
) {
  draft.data.upstreamVersionsData?.versions.forEach((sv) => {
    if (payload.ingestedNormalizedVersion === sv.normalizedVersion) {
      sv.isLocal = true;
    }
  });
}

function loadPackageDetailsCompleted(
  draft: IPackageDetailsState,
  payload: ILoadPackageDetailsCompletedPayload,
): void {
  draft.data.pkg = payload.pkg;
  draft.data.currentPackageVersion = payload.currentPackageVersion;
  draft.data.packageVersionsList = payload.versions;
}

function loadUpstreamVersionsCompleted(
  draft: IPackageDetailsState,
  payload: ILoadUpstreamVersionsCompletedPayload,
): void {
  draft.data.upstreamVersionsData = payload.upstreamVersionsData;
}
